import {Link} from "react-router-dom";
import {consoleLog} from '../../core/utilities/Utilities.js';
import {useTheme,Button,Chip,Box,Typography} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import DriveEtaRoundedIcon from '@mui/icons-material/DriveEtaRounded';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import {useNotifications} from '../components/Notification/NotificationsContext';
import EditIcon from "@mui/icons-material/Edit";
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import {styled} from '@mui/material/styles';
import {sanitizeId, getLocale, makeGridDateValueGetter, makeGridDateFormatter, getterTeamField} from './ListsUtils.js';
const file='Entities.js';
const ColorButtonSmall=styled(Button)(({theme})=>({
    color:'white',
    fontSize:'0.7125rem',
    backgroundColor:theme.palette.primary.main,
    '&:hover':{
      backgroundColor:theme.palette.primary.dark,
    },
    '& .MuiButtonGroup-firstButton': {
      borderColor: "black",
    },
    paddingLeft:'5px',
    paddingRight:'5px',
    paddingTop:'0',
    paddingBottom:'0',
}));

const LinkStyle=()=>{
    const theme=useTheme();
    const listLinkColor=theme.list_link_color;
    return {textDecoration:"none",color:listLinkColor,fontWeight:'normal'}
}

const NotificationTypeIcon=({type_db})=>{
    const {getNotificationIcon}=useNotifications();
    return getNotificationIcon(type_db);
};
const NotificationTypeText=({type_db,type})=>{
    const {getNotificationColor}=useNotifications();
    return <Typography
        variant="body2"
        sx={{
            color:getNotificationColor(type_db),
            display:"flex",
            alignItems:"center",
            lineHeight:"inherit"
        }}>{type}</Typography>
};

const withEditableIcon=(col)=>{
  if(!col.editable) return col;
  return {
    ...col,
    renderHeader:(params)=>(
      <span style={{display:"flex",alignItems:"center",gap:6}}>
        {params.colDef.headerName}
        <EditIcon fontSize="inherit" style={{opacity:0.35}}/>
      </span>
    )
  };
};

const ThemedLink=({to,onClick,children})=>{
    const theme=useTheme();
    const style={textDecoration:"none",color:theme.list_link_color,fontWeight:'normal'};
    return <Link to={to} onClick={onClick} style={style}>{children}</Link>;
};

const BooleanIcons=(params)=>{
    const theme=useTheme();
    return params.value?
        <CheckCircleRoundedIcon color="success" sx={{fontSize:'1rem'}}/>:
        <CancelRoundedIcon sx={{color:theme.palette.grey[400],fontSize:'1rem'}}/>;
};

const renderNotesField=(params,handleClickEvent)=>{
    return <Box
            sx={{
                display:'flex',
                flexDirection:'row',
                alignItems:'flex-start',
                gap:1,
                width:'100%',
            }}>
            {params.row.can_update_row&&(
            <IconButton
                size="small"
                    onClick={()=>handleClickEvent({action:'openPopup',id:params.row.id})}
                    sx={{mt:0.5}}>
                <EditNoteOutlinedIcon color="primary"/>
            </IconButton>
            )}
            <Box
                sx={{
                    wordBreak:'break-word',
                    whiteSpace:'normal',
                    flexGrow:1,
                    lineHeight:1.4,
                    fontSize:"smaller"
                }}>
            {params.row.note}
            </Box>
        </Box>
}

const RenderTeamField=(params)=>{
    const theme=useTheme();
    const primaryColor=theme.palette.primary.main;
    return <Box 
                style={{marginTop:'1px'}} 
                sx={{
                    display:'flex',
                    lignItems:'center',
                    flexWrap:'wrap',
                    gap:0.5,
                    height:'100%',
                    width:'100%',}}>
                {params?.row?.employees?.length>0?params.row.employees.map((item,index)=>(
                    <Chip
                        icon={item.driver?<DriveEtaRoundedIcon style={{color:'white'}}/>:null}
                        label={item.name}
                        key={index}
                        style={{fontSize:'0.7125rem',color:'white',backgroundColor:primaryColor}}/>)):null}
            </Box>
}

// getterTeamField — extracted to ListsUtils.js

const renderAuditBooleanToSwitch=(params)=>{
    const value=params.value;
    const isBoolean=params.row.is_boolean;
    if(isBoolean){return renderBooleanToSwitch(value);}
    return <span>{String(value)}</span>;
}

const renderBooleanToSwitch=(value)=>{
    return <Switch checked={!!value} disabled size="small" />;
}

//Matches business_rules.schedule_manager's SYNC_STATUS_* constants (preview_event_task_impact).
const SYNC_STATUS_LOCALE_KEY={
    'NOT_CUSTOMIZED':'SyncStatusNotCustomized',
    'CUSTOMIZED':'SyncStatusCustomized',
};
const renderSyncStatus=(application,value)=>{
    const localeKey=SYNC_STATUS_LOCALE_KEY[value];
    return <span>{localeKey?getLocale(application,localeKey):(value||'')}</span>;
}

export const handleLinkClick=(event)=>{
    const link=event.currentTarget.getAttribute("href");
    consoleLog({file:file,context:'handleLinkClick',arg_name:'link',arg_value:link});
};

// getLocale, makeGridDateValueGetter, makeGridDateFormatter — extracted to ListsUtils.js

export const columns={
    accounts:function(list_properties,application){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=><Link to={'/account/'+sanitizeId(params.row.id)+'/events'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.name}</Link>},
            {field:'phone',headerName:getLocale(application,'FieldPhone'),width:list_properties?.phone?.width??150,editable:false},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,},
            {field:'keys',headerName:getLocale(application,'FieldKeys'),width:list_properties?.keys?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'cleaning_products',headerName:getLocale(application,'FieldCleaningProducts'),width:list_properties?.cleaning_products?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'contract_signed',headerName:getLocale(application,'FieldContractSigned'),width:list_properties?.contract_signed?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
        ]);
    },
    account_events:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'PopupEventTitle'),width:list_properties?.reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/event/'+sanitizeId(params.row.id)+'/tasks'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.reference}</Link>},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??120,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'event_type',headerName:getLocale(application,'FieldEventType'),width:list_properties?.event_type?.width??150,editable:false},
            {field:'event_sub_type',headerName:getLocale(application,'FieldEventSubType'),width:list_properties?.event_sub_type?.width??150,editable:false},
            {field:'recurring_type',headerName:getLocale(application,'FieldRecurringType'),width:list_properties?.recurring_type?.width??150,editable:false},
            {field:'start_date',headerName:getLocale(application,'FieldStartDate'),width:list_properties?.start_date?.width??150,editable:false,},
            {field:'end_date',headerName:getLocale(application,'FieldEndDate'),width:list_properties?.end_date?.width??150,editable:false,},
            {field:'start_time',headerName:getLocale(application,'FieldStartTime'),width:list_properties?.start_time?.width??150,editable:false},
            {field:'end_time',headerName:getLocale(application,'FieldEndTime'),width:list_properties?.end_time?.width??150,editable:false},
            {field:'team',headerName:getLocale(application,'EmployeesTitle'),width:list_properties?.team?.width??300,editable:false,
                renderCell:(params)=>RenderTeamField(params),
                valueGetter:(value,row)=>getterTeamField,
            },
        ]);
    },
    account_tasks:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.id)+'/hours'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.reference}</Link>},
            {field:'rescheduled',headerName:getLocale(application,'FieldRescheduled'),width:list_properties?.rescheduled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'cancelled',headerName:getLocale(application,'FieldCancelled'),width:list_properties?.cancelled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??150,editable:false,},
            {field:'planned_date',headerName:getLocale(application,'FieldPlannedDate'),width:list_properties?.planned_date?.width??150,editable:false},
            {field:'start_at',headerName:getLocale(application,'FieldPlannedStartTime'),width:list_properties?.start_at?.width??150,editable:false,},
            {field:'end_at',headerName:getLocale(application,'FieldPlannedEndTime'),width:list_properties?.end_at?.width??150,editable:false,},
            {field:'planned_duration',headerName:getLocale(application,'FieldPlannedDuration'),width:list_properties?.planned_duration?.width??150,editable:false,},
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??150,editable:false},
            {field:'team',headerName:getLocale(application,'EmployeesTitle'),width:list_properties?.team?.width??300,editable:false,
                renderCell:(params)=>RenderTeamField(params),
                valueGetter:(value,row)=>getterTeamField,
            },
            {field:'event_reference',headerName:getLocale(application,'PopupEventTitle'),width:list_properties?.event_reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/event/'+sanitizeId(params.row.event_id)+'/tasks'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.event_reference}</Link>},
        ]);
    },
    account_notes:function(list_properties,application,handleClickEvent){
        return([
            {field:'task_id',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.task_id?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.task_id)+'/notes'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.task_reference}</Link>},
            {field:'note',headerName:getLocale(application,'FieldNote'),width:list_properties?.note?.width??250,editable:false,
                renderCell:(params)=>(renderNotesField(params,handleClickEvent)),},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false,},
            {field:'updated_at',headerName:getLocale(application,'FieldUpdatedAt'),width:list_properties?.updated_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'updated_by',headerName:getLocale(application,'FieldUpdatedBy'),width:list_properties?.updated_by?.width??150,editable:false,},
        ]);
    },
    audit:function(list_properties,application){
        return([
            {field:'entity_column',headerName:getLocale(application,'AuditField'),width:list_properties?.entity_column?.width??300,editable:false},
            {field:'value_old',headerName:getLocale(application,'AuditOldValue'),width:list_properties?.value_old?.width??300,editable:false,
                renderCell:(params)=>renderAuditBooleanToSwitch(params)},
            {field:'value_new',headerName:getLocale(application,'AuditNewValue'),width:list_properties?.value_new?.width??300,editable:false,
                renderCell:(params)=>renderAuditBooleanToSwitch(params)},
            {field:'created_at',headerName:getLocale(application,'AuditDateTime'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'AuditUser'),width:list_properties?.created_by?.width??300,editable:false},
        ]);
    },
    users:function(list_properties,application){
        return([
            {field:'login',headerName:getLocale(application,'FieldLogin'),width:list_properties?.login?.width??150,editable:false,
                renderCell:(params)=><Link to={'/user/'+sanitizeId(params.row.id)+'/worked_hours'} style={LinkStyle()}>{params.row.login}</Link>},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false},
            {field:'phone',headerName:getLocale(application,'FieldPhone'),width:list_properties?.phone?.width??150,editable:false},
            {field:'driver',headerName:getLocale(application,'FieldDriver'),width:list_properties?.driver?.width??150,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
        ]);
    },
    user_tasks:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.id)+'/hours'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.reference}</Link>},
            {field:'account_id',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.account_id?.width??300,editable:false,
                renderCell:(params)=><Link to={'/account/'+sanitizeId(params.row.account_id)+'/events'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.account_name}</Link>},
            {field:'rescheduled',headerName:getLocale(application,'FieldRescheduled'),width:list_properties?.rescheduled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'cancelled',headerName:getLocale(application,'FieldCancelled'),width:list_properties?.cancelled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??150,editable:false,},
            {field:'planned_date',headerName:getLocale(application,'FieldPlannedDate'),width:list_properties?.planned_date?.width??150,editable:false},
            {field:'start_at',headerName:getLocale(application,'FieldPlannedStartTime'),width:list_properties?.start_at?.width??150,editable:false},
            {field:'end_at',headerName:getLocale(application,'FieldPlannedEndTime'),width:list_properties?.end_at?.width??150,editable:false,},
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??150,editable:false},
            {field:'team',headerName:getLocale(application,'EmployeesTitle'),width:list_properties?.team?.width??300,editable:false,
                renderCell:(params)=>RenderTeamField(params),
                valueGetter:(value,row)=>getterTeamField,
            },
            {field:'event_reference',headerName:getLocale(application,'PopupEventTitle'),width:list_properties?.event_reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/event/'+sanitizeId(params.row.event_id)+'/tasks'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.event_reference}</Link>},
        ]);
    },
    user_travel_time:function(list_properties,application,handleClickEvent){
        return([{
            field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??100,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(params?.row?.can_update_row?<ColorButtonSmall name="update" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</ColorButtonSmall>:<span>-</span>)},
            {field:'date_str',headerName:getLocale(application,'FieldDate'),width:list_properties?.date_str?.width??150,editable:false},
            {field:'duration',headerName:getLocale(application,'FieldDuration'),width:list_properties?.duration?.width??150,editable:false},
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??150,editable:false},
        ]);
    },
    user_report_period_hours:function(list_properties,application){
        return([
            {field:'info',headerName:getLocale(application,'FieldInfo'),width:list_properties?.info?.width??150,editable:false,},
            {field:'date',headerName:getLocale(application,'FieldDate'),width:list_properties?.date?.width??150,editable:false,},
            {field:'task_reference',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.task_reference?.width??150,editable:false,display:"flex",
                renderCell:(params)=>params.row.task_id===null?'':<Link to={'/task/'+sanitizeId(params.row.task_id)+'/hours'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.task_reference}</Link>},
            {field:'account_name',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.account_name?.width??250,editable:false,display:"flex",
                renderCell:(params)=><Link to={'/account/'+sanitizeId(params.row.account_id)+'/tasks'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.account_name}</Link>},
            {field:'duration_minutes',headerName:getLocale(application,'FieldDuration'),width:list_properties?.duration_minutes?.width??150,editable:false,display:"flex"},
            {field:'duration_hh_mm',headerName:getLocale(application,'FieldDurationHHMM'),width:list_properties?.duration_hh_mm?.width??150,editable:false,display:"flex"},
        ]);
    },
    user_worked_hours:function(list_properties,application){
        return([
            {field:'type',headerName:getLocale(application,'FieldInfo'),width:list_properties?.type?.width??250,editable:false,},
            {field:'total_worked_minutes',headerName:getLocale(application,'FieldDuration'),width:list_properties?.total_worked_minutes?.width??200,editable:false,display:"flex"},
            {field:'duration_hh_mm',headerName:getLocale(application,'FieldDurationHHMM'),width:list_properties?.duration_hh_mm?.width??200,editable:false,display:"flex"},
        ]);
    },
    tasks:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.id)+'/hours'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.reference}</Link>},
            {field:'account_name',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.account_name?.width??300,editable:false,
                renderCell:(params)=><Link to={'/account/'+sanitizeId(params.row.account_id)+'/events'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.account_name}</Link>},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??150,editable:false,},
            {field:'planned_date',headerName:getLocale(application,'FieldPlannedDate'),width:list_properties?.planned_date?.width??150,editable:false,},
            {field:'start_at',headerName:getLocale(application,'FieldPlannedStartTime'),width:list_properties?.start_at?.width??150,editable:false,},
            {field:'end_at',headerName:getLocale(application,'FieldPlannedStartTime'),width:list_properties?.end_at?.width??150,editable:false,},
            {field:'planned_duration',headerName:getLocale(application,'FieldPlannedDuration'),width:list_properties?.planned_duration?.width??150,editable:false,},
            {field:'rescheduled',headerName:getLocale(application,'FieldRescheduled'),width:list_properties?.rescheduled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'cancelled',headerName:getLocale(application,'FieldCancelled'),width:list_properties?.cancelled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'team',headerName:getLocale(application,'EmployeesTitle'),width:list_properties?.team?.width??300,editable:false,
                renderCell:(params)=>RenderTeamField(params),
                valueGetter:(value,row)=>getterTeamField,
            },
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??150,editable:false},
        ]);
    },
    task_employees:function(list_properties,application,handleClickEvent){
        return([{
            field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??150,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(params?.row?.can_update_row?<ColorButtonSmall name="update" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</ColorButtonSmall>:<span>-</span>),},            
            {field:'user_name',headerName:getLocale(application,'FooterUser'),width:list_properties?.user_name?.width??300,editable:false,
                renderCell:(params)=><Link to={'/user/'+sanitizeId(params.row.system_user_id)+'/worked_hours'} style={LinkStyle()}>{params.row.user_name}</Link>},
            {field:'actual_start_time',headerName:getLocale(application,'FieldStartTime'),width:list_properties?.actual_start_time?.width??150,editable:false},
            {field:'actual_end_time',headerName:getLocale(application,'FieldEndTime'),width:list_properties?.actual_end_time?.width??150,editable:false,},
            {field:'total_worked_duration',headerName:getLocale(application,'FieldTotalWorkedTime'),width:list_properties?.total_worked_duration?.width??150,editable:false,},
            {field:'total_worked_minutes',headerName:getLocale(application,'FieldTotalWorkedTimeMin'),width:list_properties?.total_worked_minutes?.width??150,editable:false,type:"number"},
        ]);
    },
    task_notes:function(list_properties,application,handleClickEvent){
        return([
            {field:'note',headerName:getLocale(application,'FieldNote'),width:list_properties?.note?.width??250,editable:false,
                renderCell:(params)=>(renderNotesField(params,handleClickEvent)),},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false,},
            {field:'updated_at',headerName:getLocale(application,'FieldUpdatedAt'),width:list_properties?.updated_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'updated_by',headerName:getLocale(application,'FieldUpdatedBy'),width:list_properties?.updated_by?.width??150,editable:false,},
        ]);
    },
    task_notes_history:function(list_properties,application,handleClickEvent){
        return([
            {field:'task_id',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.task_id?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.task_id)+'/notes'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.task_reference}</Link>},
            {field:'note',headerName:getLocale(application,'FieldNote'),width:list_properties?.note?.width??250,editable:false,
                renderCell:(params)=>(renderNotesField(params,handleClickEvent)),},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false,},
            {field:'updated_at',headerName:getLocale(application,'FieldUpdatedAt'),width:list_properties?.updated_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'updated_by',headerName:getLocale(application,'FieldUpdatedBy'),width:list_properties?.updated_by?.width??150,editable:false,},
        ]);
    },
    notes:function(list_properties,application,handleClickEvent){
        return([
            {field:'account_id',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.account_id?.width??250,editable:false,
                renderCell:(params)=><Link to={'/account/'+sanitizeId(params.row.account_id)+'/notes'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.account_name}</Link>},
            {field:'task_id',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.task_id?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.task_id)+'/notes'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.task_reference}</Link>},
            {field:'note',headerName:getLocale(application,'FieldNote'),width:list_properties?.note?.width??250,editable:false,
                renderCell:(params)=>(renderNotesField(params,handleClickEvent)),},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false,},
            {field:'updated_at',headerName:getLocale(application,'FieldUpdatedAt'),width:list_properties?.updated_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'updated_by',headerName:getLocale(application,'FieldUpdatedBy'),width:list_properties?.updated_by?.width??150,editable:false,},
        ]);
    },
    events:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'PopupEventTitle'),width:list_properties?.reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/event/'+sanitizeId(params.row.id)+'/tasks'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.reference}</Link>},
            {field:'account_name',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.account_name?.width??300,editable:false,
                renderCell:(params)=><Link to={'/account/'+sanitizeId(params.row.account_id)+'/events'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.account_name}</Link>},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??120,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'event_type',headerName:getLocale(application,'FieldEventType'),width:list_properties?.event_type?.width??150,editable:false},
            {field:'event_sub_type',headerName:getLocale(application,'FieldEventSubType'),width:list_properties?.event_sub_type?.width??150,editable:false},
            {field:'recurring_type',headerName:getLocale(application,'FieldRecurringType'),width:list_properties?.recurring_type?.width??150,editable:false},
            {field:'start_date',headerName:getLocale(application,'FieldStartDate'),width:list_properties?.start_date?.width??150,editable:false,},
            {field:'end_date',headerName:getLocale(application,'FieldEndDate'),width:list_properties?.end_date?.width??150,editable:false,},
            {field:'start_time',headerName:getLocale(application,'FieldStartTime'),width:list_properties?.start_time?.width??150,editable:false},
            {field:'end_time',headerName:getLocale(application,'FieldEndTime'),width:list_properties?.end_time?.width??150,editable:false},
            {field:'team',headerName:getLocale(application,'EmployeesTitle'),width:list_properties?.team?.width??300,editable:false,
                renderCell:(params)=>RenderTeamField(params),
                valueGetter:(value,row)=>getterTeamField,
            },
        ]);
    },
    event_tasks:function(list_properties,application){
        return([
            {field:'reference',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.id)+'/hours'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.reference}</Link>},
            {field:'rescheduled',headerName:getLocale(application,'FieldRescheduled'),width:list_properties?.rescheduled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'cancelled',headerName:getLocale(application,'FieldCancelled'),width:list_properties?.cancelled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??150,editable:false,},
            {field:'planned_date',headerName:getLocale(application,'FieldPlannedDate'),width:list_properties?.planned_date?.width??150,editable:false},
            {field:'start_at',headerName:getLocale(application,'FieldPlannedStartTime'),width:list_properties?.start_at?.width??150,editable:false,},
            {field:'end_at',headerName:getLocale(application,'FieldPlannedEndTime'),width:list_properties?.end_at?.width??150,editable:false,},
            {field:'planned_duration',headerName:getLocale(application,'FieldPlannedDuration'),width:list_properties?.planned_duration?.width??150,editable:false,},
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??150,editable:false},
            {field:'team',headerName:getLocale(application,'EmployeesTitle'),width:list_properties?.team?.width??300,editable:false,
                renderCell:(params)=>RenderTeamField(params),
                valueGetter:(value,row)=>getterTeamField,
            },            
        ]);
    },

    tasks_update_event:function(list_properties,application,handleClickEvent,entityRules,userData){
        const isManager=userData?.role_name==='manager'||userData?.role_name==='admin';
        return([
            {field:'reference',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.id)+'/hours'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.reference}</Link>},
            {field:'sync_status',headerName:getLocale(application,'FieldSyncStatus'),width:list_properties?.sync_status?.width??220,editable:false,
                renderCell:(params)=>renderSyncStatus(application,params.value)},
            {field:'rescheduled',headerName:getLocale(application,'FieldRescheduled'),width:list_properties?.rescheduled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            //"Team/Time Manually Customized" is manager-only info, same visibility boundary as
            //DRAFT-status tasks - only include these two columns at all for a manager.
            ...(isManager?[
                {field:'schedule_overridden',headerName:getLocale(application,'FieldScheduleOverridden'),width:list_properties?.schedule_overridden?.width??200,editable:false,
                    renderCell:(params)=>renderBooleanToSwitch(params.value)},
                {field:'team_overridden',headerName:getLocale(application,'FieldTeamOverridden'),width:list_properties?.team_overridden?.width??200,editable:false,
                    renderCell:(params)=>renderBooleanToSwitch(params.value)},
                {field:'duration_overridden',headerName:getLocale(application,'FieldPlannedDurationOverridden'),width:list_properties?.duration_overridden?.width??200,editable:false,
                    renderCell:(params)=>renderBooleanToSwitch(params.value)},
            ]:[]),
            {field:'cancelled',headerName:getLocale(application,'FieldCancelled'),width:list_properties?.cancelled?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??150,editable:false,},
            {field:'planned_date',headerName:getLocale(application,'FieldPlannedDate'),width:list_properties?.planned_date?.width??150,editable:false},
            {field:'start_at',headerName:getLocale(application,'FieldPlannedStartTime'),width:list_properties?.start_at?.width??150,editable:false,},
            {field:'end_at',headerName:getLocale(application,'FieldPlannedEndTime'),width:list_properties?.end_at?.width??150,editable:false,},
            {field:'planned_duration',headerName:getLocale(application,'FieldPlannedDuration'),width:list_properties?.planned_duration?.width??150,editable:false,},
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??150,editable:false},
            {field:'team',headerName:getLocale(application,'EmployeesTitle'),width:list_properties?.team?.width??300,editable:false,
                renderCell:(params)=>RenderTeamField(params),
                valueGetter:(value,row)=>getterTeamField,
            },
        ]);
    },
    tasks_sync_account_field:function(list_properties,application,handleClickEvent,entityRules,userData){
        return([
            {field:'reference',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.reference?.width??150,editable:false,
                renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.id)+'/hours'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.reference}</Link>},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??150,editable:false,},
            {field:'planned_date',headerName:getLocale(application,'FieldPlannedDate'),width:list_properties?.planned_date?.width??150,editable:false},
            {field:'start_at',headerName:getLocale(application,'FieldPlannedStartTime'),width:list_properties?.start_at?.width??150,editable:false,},
            {field:'end_at',headerName:getLocale(application,'FieldPlannedEndTime'),width:list_properties?.end_at?.width??150,editable:false,},
            {field:'bed_linen_in_out',headerName:getLocale(application,'FieldBedLinenInOut'),width:list_properties?.bed_linen_in_out?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
            {field:'cleaning_cloths',headerName:getLocale(application,'FieldCleaningCloths'),width:list_properties?.cleaning_cloths?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
        ]);
    },
    report_account_period_hours:function(list_properties,application){
        return([
            {field:'account_name',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.account_name?.width??250,editable:false,display:"flex",renderCell:(params)=><Link to={'/account/'+sanitizeId(params.row.account_id)+'/tasks'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.account_name}</Link>},
            {field:'duration_minutes',headerName:getLocale(application,'FieldDuration'),width:list_properties?.duration_minutes?.width??150,editable:false,display:"flex"},
            {field:'duration_hh_mm',headerName:getLocale(application,'FieldDurationHHMM'),width:list_properties?.duration_hh_mm?.width??150,editable:false,display:"flex"},
        ]);
    },
    work_time_entries:function(list_properties,application,handleClickEvent){
        return([{
            field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??100,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(params?.row?.can_update_row?<ColorButtonSmall name="update" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</ColorButtonSmall>:<span>-</span>)},
            {field:'date',headerName:getLocale(application,'FieldDate'),width:list_properties?.date?.width??120,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),valueFormatter:makeGridDateFormatter(application)},
            {field:'category',headerName:getLocale(application,'FieldWorkCategory'),width:list_properties?.category?.width??160,editable:false},
            {field:'start_time',headerName:getLocale(application,'FieldStartTime'),width:list_properties?.start_time?.width??110,editable:false},
            {field:'end_time',headerName:getLocale(application,'FieldEndTime'),width:list_properties?.end_time?.width??110,editable:false},
            {field:'duration_hh_mm',headerName:getLocale(application,'FieldDurationHHMM'),width:list_properties?.duration_hh_mm?.width??130,editable:false},
            {field:'ended_by',headerName:getLocale(application,'FieldEndedBy'),width:list_properties?.ended_by?.width??170,editable:false,display:'flex',
                renderCell:(params)=>params.value?<Chip size="small" label={params.value} color={params.row.ended_by_db==='AUTO'?'error':(params.row.ended_by_db==='PAUSE'?'warning':'default')}/>:''},
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??250,editable:false},
        ]);
    },
    report_workday_hours:function(list_properties,application){
        //one column per work type, taken from the Work_Category list of values (sick leave has its own column)
        const categories=Object.entries(application?.globalData?.list_of_values?.Work_Category??{}).filter(([name])=>name!=='SICK_LEAVE');
        return([
            {field:'user_name',headerName:getLocale(application,'FieldName'),width:list_properties?.user_name?.width??200,editable:false,display:"flex",
                renderCell:(params)=><Link to={'/user/'+sanitizeId(params.row.system_user_id)+'/workday'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.user_name}</Link>},
            {field:'date',headerName:getLocale(application,'FieldDate'),width:list_properties?.date?.width??120,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),valueFormatter:makeGridDateFormatter(application)},
            {field:'first_start',headerName:getLocale(application,'FieldFirstStart'),width:list_properties?.first_start?.width??130,editable:false},
            {field:'last_end',headerName:getLocale(application,'FieldLastEnd'),width:list_properties?.last_end?.width??130,editable:false},
            ...categories.map(([name,value])=>(
                {field:'cat_'+name,headerName:value,width:list_properties?.['cat_'+name]?.width??130,editable:false}
            )),
            {field:'worked_hh_mm',headerName:getLocale(application,'FieldWorkedTime'),width:list_properties?.worked_hh_mm?.width??120,editable:false},
            {field:'break_hh_mm',headerName:getLocale(application,'FieldBreakTime'),width:list_properties?.break_hh_mm?.width??120,editable:false},
            {field:'sick_hh_mm',headerName:getLocale(application,'FieldSickLeaveTime'),width:list_properties?.sick_hh_mm?.width??120,editable:false},
            {field:'auto_closed',headerName:getLocale(application,'FieldAutoClosed'),width:list_properties?.auto_closed?.width??150,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
        ]);
    },
    report_task_employee_hours_period:function(list_properties,application){
        return([
            {field:'task_reference',headerName:getLocale(application,'PopupTaskTitle'),width:list_properties?.task_reference?.width??120,editable:false,display:"flex",renderCell:(params)=><Link to={'/task/'+sanitizeId(params.row.task_id)+'/hours'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.task_reference}</Link>},
            {field:'account_name',headerName:getLocale(application,'FieldAccountName'),width:list_properties?.account_name?.width??230,editable:false,display:"flex",renderCell:(params)=><Link to={'/account/'+sanitizeId(params.row.account_id)+'/tasks'} onClick={handleLinkClick} style={LinkStyle()}>{params.row.account_name}</Link>},
            {field:'duration_minutes',headerName:getLocale(application,'FieldDuration'),width:list_properties?.duration_minutes?.width??200,editable:false,display:"flex"},
            {field:'duration_hh_mm',headerName:getLocale(application,'FieldDurationHHMM'),width:list_properties?.duration_hh_mm?.width??200,editable:false,display:"flex"},
            {field:'team',headerName:getLocale(application,'EmployeesTitle'),width:list_properties?.team?.width??300,editable:false,
                renderCell:(params)=>RenderTeamField(params),
                valueGetter:(value,row)=>getterTeamField,
            },
            //DG
        ]);
    },

    list_of_values:function(list_properties,application){
        return([
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??150,editable:false},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??150,editable:false},
            {field:'language',headerName:getLocale(application,'FieldLanguage'),width:list_properties?.language?.width??150,editable:false},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??150,editable:false},
            {field:'sorting_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sorting_order?.width??150,editable:false,type:'number'},
        ]);
    },
    locales:function(list_properties,application){
        return([
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??150,editable:false},
            {field:'language',headerName:getLocale(application,'FieldLanguage'),width:list_properties?.language?.width??150,editable:false},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??150,editable:false},
        ]);
    },
    holidays:function(list_properties,application){
        return([
            {field:'date_str',headerName:getLocale(application,'FieldDate'),width:list_properties?.date?.width??150,editable:false},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??150,editable:false},
            {field:'public',headerName:getLocale(application,'FieldPublic'),width:list_properties?.public?.width??200,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)},
        ]);
    },
    notifications:function(list_properties,application){
        return([
            {field:'system_user_login',headerName:getLocale(application,'FooterUser'),width:list_properties?.system_user_login?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/user/'+sanitizeId(params.row.system_user_id)+'/audit'} onClick={handleLinkClick}>{params.row.system_user_login}</ThemedLink>
            },
            {field:'title',headerName:getLocale(application,'FieldTitle'),width:list_properties?.title?.width??250,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/notification/'+sanitizeId(params.row.id)} onClick={handleLinkClick}>{params.row.title}</ThemedLink>
            },
            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??250,editable:false},
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??250,editable:false},
            {field:'link',headerName:getLocale(application,'FieldLink'),width:list_properties?.link?.width??250,editable:false},
            {field:'table_name',headerName:getLocale(application,'FieldTableName'),width:list_properties?.table_name?.width??250,editable:false},
            {field:'record_id',headerName:getLocale(application,'FieldId'),width:list_properties?.record_id?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'read',headerName:getLocale(application,'FieldRead'),width:list_properties?.read?.width??150,editable:false,type:'boolean',
                renderCell:(params)=>BooleanIcons(params),
            },
            {field:'read_at',headerName:getLocale(application,'FieldReadAt'),width:list_properties?.read_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
        ]);
    },
    my_notifications:function(list_properties,application,handleClickEvent){
        return([
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??180,editable:false,
                renderCell:(params)=>(
                    <Box sx={{display:"flex",alignItems:"center",gap:1}}>
                        <NotificationTypeIcon type_db={params.row.type_db} />
                        <NotificationTypeText type_db={params.row.type_db} type={params.row.type}/>
                    </Box>
                )
            },
            {field:'title',headerName:getLocale(application,'FieldTitle'),width:list_properties?.title?.width??250,editable:false,
                renderCell:(params)=>
                    <ThemedLink 
                        onClick={(e)=>{
                            e.stopPropagation();
                            e.preventDefault();
                            handleClickEvent({action:'invoke_method',args:{method:"myNotificationDrilldown",recordId:params.row.id,read:params.row.read}})
                        }
                    }>
                        {params.row.title}
                    </ThemedLink>
            },

            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'read',headerName:getLocale(application,'FieldRead'),width:list_properties?.read?.width??150,editable:false,type:'boolean',
                renderCell:(params)=>BooleanIcons(params),
            },
            {field:'read_at',headerName:getLocale(application,'FieldReadAt'),width:list_properties?.read_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
        ]);
    },
    app_roles:function(list_properties,application,handleClickEvent,entityRules){
        const cols=[
            {field:'role',headerName:getLocale(application,'FieldRole'),width:list_properties?.role?.width??150,editable:false},
            {field:'main_color',headerName:getLocale(application,'FieldMainColor'),width:list_properties?.main_color?.width??120,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("main_color")},
            {field:'list_link_color',headerName:getLocale(application,'FieldListLinkColor'),width:list_properties?.list_link_color?.width??130,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("list_link_color")},
            {field:'landing_page',headerName:getLocale(application,'FieldLandingPage'),width:list_properties?.landing_page?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("landing_page")},
        ];
        return cols.map(withEditableIcon);
    },
    app_configs:function(list_properties,application,handleClickEvent,entityRules){
        const canUpdate=entityRules?.can_update;
        const editable=(field)=>canUpdate&&(entityRules?.can_update_list_fields?.includes(field)??false);
        const cols=[
            {field:'category',headerName:getLocale(application,'FieldCategory'),width:list_properties?.category?.width??160,editable:editable('category')},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??280,editable:editable('name')},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??200,editable:editable('value'),
                renderCell:(params)=>params.row.is_sensitive
                    ?<span style={{letterSpacing:'2px',color:'var(--color-text-secondary,#888)'}}>{'●●●●●●●●'}</span>
                    :params.value
            },
            {field:'value_type',headerName:getLocale(application,'FieldValueType'),width:list_properties?.value_type?.width??100,editable:editable('value_type')},
            {field:'core',headerName:getLocale(application,'FieldCore'),width:list_properties?.core?.width??80,editable:false,type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'ui_available',headerName:getLocale(application,'FieldUiAvailable'),width:list_properties?.ui_available?.width??100,editable:editable('ui_available'),type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'is_sensitive',headerName:getLocale(application,'FieldSensitive'),width:list_properties?.is_sensitive?.width??100,editable:editable('is_sensitive'),type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??300,editable:editable('description')},
        ];
        return cols.map(withEditableIcon);
    },
    app_menus:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu/'+sanitizeId(params.row.id)+'/items'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'menu_label',headerName:getLocale(application,'FieldMenuLabel'),width:list_properties?.menu_label?.width??200,editable:false},
            {field:'menu_color',headerName:getLocale(application,'FieldMenuColor'),width:list_properties?.menu_color?.width??130,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left"},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_items:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu_item/'+sanitizeId(params.row.id)+'/routes'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'label',headerName:getLocale(application,'FieldLabel'),width:list_properties?.label?.width??200,editable:false},
            {field:'icon_name',headerName:getLocale(application,'FieldIconName'),width:list_properties?.icon_name?.width??150,editable:false},
            {field:'visible',headerName:getLocale(application,'FieldVisible'),width:list_properties?.visible?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left"},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_item_routes:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu_item_route/'+sanitizeId(params.row.id)+'/route_tabs'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'path',headerName:getLocale(application,'FieldPath'),width:list_properties?.path?.width??250,editable:false},
            {field:'is_default',headerName:getLocale(application,'FieldDefault'),width:list_properties?.is_default?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_item_route_tabs:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false},
            {field:'label',headerName:getLocale(application,'FieldLabel'),width:list_properties?.label?.width??200,editable:false},
            {field:'path',headerName:getLocale(application,'FieldPath'),width:list_properties?.path?.width??150,editable:false},
            {field:'is_default',headerName:getLocale(application,'FieldDefault'),width:list_properties?.is_default?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left"},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
};

export const listPreDefaultFilter=(globalData)=>{
    return {
        accounts:{
            items:[
                {
                    field:'status',
                    operator:'isAnyOf',
                    value:[globalData?.list_of_values?.Account_Status.ACTIVE]
                },
            ],
        },
    }
}

export const listPreDefaultSorting={
    //No Pre Defaults So Far!
    /*
    accounts:[{
        field:'name',
        sort:'asc',
    }],
    */
}